import React from 'react';
import { observer } from 'mobx-react-lite';
import { Badge, Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useFeatureFlag, useL10n } from '@uplift/hooks';
import { SideBarType } from '@uplift/types';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';

import { NavigationItem } from './navigation-item.component';
import { useStyles } from './side-bar.styles';
import { useMessagingContext } from '../../../Messaging/MessagingProvider.component';


const ObservedSidebar: React.FC<SideBarType> = observer(
  ({ mainNavConfig, infoNavConfig, toggleSideBar }) => {
    const IN_APP_MESSAGING = useFeatureFlag('IN_APP_MESSAGING');
    const messaging = useMessagingContext();
    const l10n = useL10n();
    const { classes } = useStyles();

    return (
      <Box className={classes.sidebar}>
        <Box className={classes.wrapper}>
          <List className={classes.list}>
            {mainNavConfig?.map((item) => (
              <NavigationItem
                key={item.to}
                exact={item.exact ?? true}
                to={item.to}
                text={item.name}
                icon={item.icon}
                disabled={item.disabled}
                restricted={item.restricted}
                activeIcon={item.activeIcon}
                activeClassName={classes.activeNav}
                toggleSideBar={toggleSideBar}
                onDisabledClick={item.onDisabledClick}
              />
            ))}
            {IN_APP_MESSAGING ? (
              <ListItemButton className={classes.listItem} onClick={() => messaging.openModal()} sx={{ color: 'rgba(0, 0, 0, 0.54)', }}>
                <ListItemIcon>
                  <Badge badgeContent={messaging.unreadMessagesCount} color="error">
                    <MailOutlinedIcon />
                  </Badge>
                </ListItemIcon>
                <ListItemText primary="Messages" />
              </ListItemButton>
            ) : null}
          </List>

          {infoNavConfig && (
            <>
              <List className={classes.list}>
                <ListItem className={classes.listItem}>
                  <div className={classes.tools}>{l10n.general.tools}</div>
                </ListItem>
              </List>

              <List className={classes.list}>
                {infoNavConfig.map((item) => {
                  if (item.to === '') {
                    return <span key={item.to} />;
                  } else {
                    return (
                      <NavigationItem
                        key={item.to}
                        exact={item.exact ?? true}
                        to={item.to}
                        text={item.name}
                        icon={item.icon}
                        activeClassName={classes.activeNav}
                        toggleSideBar={toggleSideBar}
                      />
                    );
                  }
                })}
              </List>
            </>
          )}
        </Box>
        <Box height="100%" />
        <Box className={classes.contactSupportContainer}>
          <a
            className={classes.contactSupportLink}
            href="https://client-help.joinuplift.co/"
            target="_blank"
            rel="noreferrer"
          >
            <ContactSupportOutlinedIcon
              className={classes.contactSupportIcon}
            />
            <Typography className={classes.contactSupportText}>
              {l10n.general.helpCenter}
            </Typography>
          </a>
        </Box>

        <Box className={classes.privacy}>
          <a
            href="https://www.joinuplift.co/tos"
            rel="noreferrer"
            target="_blank"
          >
            {l10n.general.terms}
          </a>
          <p className={classes.dot}>•</p>
          <a
            href="https://www.joinuplift.co/privacy"
            rel="noreferrer"
            target="_blank"
          >
            {l10n.general.privacy}
          </a>
        </Box>
      </Box>
    );
  }
);

export const SideBar = ({
  infoNavConfig,
  mainNavConfig,
  toggleSideBar,
}: SideBarType) => {
  return (
    <ObservedSidebar
      infoNavConfig={infoNavConfig}
      mainNavConfig={mainNavConfig}
      toggleSideBar={toggleSideBar}
    />
  );
};
